import * as Ariakit from '@ariakit/react';
import Button, { ButtonVariant } from '@ben/orion/deprecated-button';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import { fsMaskForHSBCInstance } from '@ben/shared/constants';
import type { IModularBenefitContact } from '../../../types/modular-benefit';
import type { FC, KeyboardEvent } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css, useTheme } from 'styled-components';

interface IEditPersonButtonProps {
  navigateEditDependent: (id: string) => void;
  contacts: IModularBenefitContact[];
  isBeneficiaryComponent?: boolean;
}

const EditPersonButton: FC<IEditPersonButtonProps> = ({
  navigateEditDependent,
  contacts,
  isBeneficiaryComponent = false,
}: IEditPersonButtonProps) => {
  const popover = Ariakit.usePopoverStore();
  const { t } = useTranslation();
  const theme = useTheme();

  const isExpanded = popover.useState().open;

  const handleKeyDown = useCallback(
    (event: KeyboardEvent, id: number) => {
      const eventKey = event.key;
      if (eventKey === 'Enter' || eventKey === ' ') {
        event.preventDefault();
        event.stopPropagation();
        navigateEditDependent(String(id));
      }
    },
    [navigateEditDependent]
  );

  return (
    <>
      <StyledDisclosure
        store={popover}
        tabIndex={-1}
        aria-expanded={isExpanded ? 'true' : 'false'}
      >
        <StyledButton
          forwardedAs="p"
          variant={ButtonVariant.secondary}
          label={
            isBeneficiaryComponent
              ? t('addNewPersonBlock.editPeople')
              : t('contacts.editDependants')
          }
          minWidth="186px"
          tabIndex={0}
        />
      </StyledDisclosure>
      <StyledPopover store={popover} role="list">
        {contacts?.map((contact) => (
          <div role="listitem" key={contact.id}>
            <StyledTypography
              variant={TypographyVariant.bodyMd2}
              color={theme.palette.neutral[100]}
              onClick={() => navigateEditDependent(String(contact.id))}
              key={contact.id}
              className={fsMaskForHSBCInstance}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => handleKeyDown(event, contact.id)}
            >
              {`${contact?.firstName} ${contact?.lastName}`}
            </StyledTypography>
          </div>
        ))}
      </StyledPopover>
    </>
  );
};

export default EditPersonButton;

const StyledDisclosure = styled(Ariakit.PopoverDisclosure)`
  ${({ theme }) => css`
    text-decoration: none;
    background-color: ${theme.palette.neutral['00']};
    border: none;
    cursor: pointer;
    padding: 0 !important;
    outline: none !important;
    width: fit-content;
  `}
`;

const StyledPopover = styled(Ariakit.Popover)`
  ${({ theme }) => css`
    box-shadow: 0px 4px 10px ${theme.palette.black(0.1)};
    border-radius: 10px;
    background-color: ${theme.palette.neutral['00']};
    padding: ${theme.spacing(2)};
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2.5)};
    min-width: 180px;
    z-index: 1000;
  `}
`;

const StyledTypography = styled(Typography)`
  ${({ theme }) => css`
    cursor: pointer;

    &:hover,
    &:focus {
      color: ${theme.palette.neutral[90]};
    }
  `}
`;

const StyledButton = styled(Button)`
  white-space: nowrap;
`;
